import { Link, useParams } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Seo, SITE_URL } from "@/components/Seo";
import { FadeUp, Overline } from "@/components/Reveal";
import { Magnetic } from "@/components/Magnetic";
import { productBySlug, PRODUCTS } from "@/data/products";
import NotFound from "./NotFound";

export default function ProductPage() {
  const { slug } = useParams();
  const product = productBySlug(slug);
  if (!product) return <NotFound />;

  const related = PRODUCTS.filter((p) => product.related.includes(p.slug));
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Products", item: `${SITE_URL}/products` },
      { "@type": "ListItem", position: 3, name: product.name, item: `${SITE_URL}/products/${product.slug}` },
    ],
  };

  return (
    <div className="bg-white min-h-screen" data-testid={`product-page-${product.slug}`}>
      <Seo title={product.title} description={product.description} jsonLd={jsonLd} />
      <Nav />
      <main className="pt-44 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-xs text-midnight/40 mb-8">
            <Link to="/" className="hover:text-gold">Home</Link> / <Link to="/products" className="hover:text-gold">Products</Link> / <span className="text-midnight/70">{product.name}</span>
          </p>

          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <FadeUp>
              <Overline>{product.name}</Overline>
              <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-midnight">{product.h1}</h1>
              <p className="text-midnight/60 text-lg mt-6 leading-relaxed">{product.intro}</p>

              <h2 className="font-heading text-xl font-medium text-midnight mt-10 mb-5">What we supply</h2>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {product.offerings.map((o) => (
                  <li key={o} className="flex items-center gap-3 text-midnight/70 text-sm">
                    <span className="w-4 h-px bg-gold shrink-0" />{o}
                  </li>
                ))}
              </ul>

              <h2 className="font-heading text-xl font-medium text-midnight mt-10 mb-5">Typical buyers</h2>
              <div className="flex flex-wrap gap-2">
                {product.buyers.map((b) => (
                  <span key={b} className="text-xs bg-[#FAFAFA] hairline text-midnight/65 px-3.5 py-2">{b}</span>
                ))}
              </div>

              <Magnetic className="mt-10">
                <Link
                  to={`/request?category=${encodeURIComponent(product.name === "AI & GPU Compute" ? "Enterprise GPU" : product.name)}`}
                  data-testid="product-rfq-cta"
                  className="inline-block bg-midnight text-white text-sm font-medium px-8 py-4 hover:bg-midnight-light transition-colors"
                >
                  Request {product.name}
                </Link>
              </Magnetic>
            </FadeUp>

            <FadeUp delay={0.1}>
              {product.image && (
                <div className="hairline shadow-soft overflow-hidden mb-8">
                  <img src={product.image} alt={product.imageAlt} width="1200" height="675" className="w-full h-auto" loading="eager" />
                </div>
              )}
              <div className="bg-[#FAFAFA] hairline p-8">
                <h2 className="font-heading text-lg font-medium text-midnight mb-3">Sourcing &amp; qualification</h2>
                <p className="text-sm text-midnight/60 leading-relaxed">{product.sourcing}</p>
                <h3 className="font-heading text-base font-medium text-midnight mt-6 mb-2">Compliance</h3>
                <p className="text-sm text-midnight/55 leading-relaxed">{product.compliance}</p>
                <p className="text-sm text-midnight/55 leading-relaxed mt-4">
                  Manufacturer part numbers, quantities and destinations can be submitted directly through our{" "}
                  <Link to="/request" className="text-gold hover:text-midnight transition-colors">commercial requirement form</Link>.
                </p>
              </div>
            </FadeUp>
          </div>

          {related.length > 0 && (
            <div className="mt-20 pt-10 border-t border-midnight/10">
              <h2 className="font-heading text-xl font-medium text-midnight mb-6">Related categories</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link key={r.slug} to={`/products/${r.slug}`} data-testid={`related-${r.slug}`} className="bg-white hairline p-6 group hover:border-gold/40 transition-colors">
                    <p className="font-heading font-medium text-midnight group-hover:text-gold transition-colors">{r.name}</p>
                    <p className="text-xs text-midnight/50 mt-2">{r.h1}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
